import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { JourneyApiService, registerCacheClearFunction } from '../../services/journeyApiService';
import { Journey, JourneyStep } from '../../types/journey';
import './JourneyHomeSection.css';

// Cache for journey data to avoid repeated API calls
const journeyCache = {
  data: null as { journey: Journey; steps: JourneyStep[] } | null,
  timestamp: 0,
  CACHE_DURATION: 5 * 60 * 1000 // 5 minutes
};

// Export function to clear cache when journey is updated
export const clearJourneyCache = () => {
  journeyCache.data = null;
  journeyCache.timestamp = 0;
};

// Register the cache clearing function with the API service
registerCacheClearFunction(clearJourneyCache);

export const JourneyHomeSection: React.FC = () => {
  const { user } = useAuth();
  const { t, uiLanguage } = useLanguage();
  const navigate = useNavigate();
  const [journey, setJourney] = useState<Journey | null>(null);
  const [steps, setSteps] = useState<JourneyStep[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchAttempted = useRef(false);

  useEffect(() => {
    const fetchActiveJourney = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      // Prevent duplicate fetches
      if (fetchAttempted.current) return;
      fetchAttempted.current = true;

      try {
        // Check cache first
        const now = Date.now();
        if (journeyCache.data && (now - journeyCache.timestamp) < journeyCache.CACHE_DURATION) {
          setJourney(journeyCache.data.journey);
          setSteps(journeyCache.data.steps);
          setLoading(false);
          return;
        }

        const response = await JourneyApiService.getUserActiveJourney();
        if (response) {
          setJourney(response.journey);
          setSteps(response.steps);
          // Update cache
          journeyCache.data = response;
          journeyCache.timestamp = now;
        }
      } catch (error) {
        console.error('Error fetching active journey:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchActiveJourney();
  }, [user]);

  if (loading) {
    return (
      <div className="journey-home-section loading">
        <div className="section-content horizontal">
          <div className="journey-info-section">
            <div className="skeleton-header"></div>
            <div className="skeleton-progress"></div>
          </div>
          <div className="skeleton-step"></div>
          <div className="skeleton-actions">
            <div className="skeleton-button"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!journey) {
    // No journey - show CTA
    return (
      <div className="journey-home-section no-journey">
        <div className="section-content">
          <div className="section-icon">
            <i className="bi bi-map"></i>
          </div>
          <div className="section-info">
            <h3>{t('journeyHome.startYourJourney')}</h3>
            <p>{t('journeyHome.getGuidance')}</p>
          </div>
          <Link to="/startup-journey/setup" className="section-cta">
            <i className="bi bi-plus-circle"></i>
            {t('journeyHome.createJourney')}
          </Link>
        </div>
      </div>
    );
  }

  // Has journey - show progress
  const completedSteps = steps.filter(s => s.status === 'completed').length;
  const totalSteps = steps.length;
  const progressPercentage = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
  const currentStep = steps.find(s => s.status === 'current');

  /**
   * Get translated step title based on user's language preference
   * Falls back to English if Chinese translation is not available
   */
  const getStepTitle = (step: JourneyStep): string => {
    if (uiLanguage === 'zh' && step.title_zh) {
      return step.title_zh;
    }
    return step.title;
  };

  /**
   * Get translated action text based on user's language preference
   * Falls back to English if Chinese translation is not available
   */
  const getActionText = (action: any): string => {
    if (uiLanguage === 'zh' && action.text_zh) {
      return action.text_zh;
    }
    return action.text;
  };

  const getNextAction = () => {
    if (!currentStep || !currentStep.actions) return null;
    if (currentStep.actions.length === 0) return null;
    return currentStep.actions[0];
  };

  const nextAction = getNextAction();

  return (
    <div className="journey-home-section active-journey">
      <div className="section-content horizontal">
        {/* Left: Journey Info */}
        <div className="journey-info-section">
          <div className="journey-header">
            <i className="bi bi-map"></i>
            <div>
              <h3>{t('journeyHome.yourJourney')}</h3>
              <span className="journey-market">{journey.market}</span>
            </div>
          </div>
          
          <div className="journey-progress-compact">
            <div className="progress-header">
              <span className="progress-label">{t('journeyHome.progress')}</span>
              <span className="progress-percentage">{Math.round(progressPercentage)}%</span>
            </div>
            <div className="progress-bar-compact">
              <div 
                className="progress-fill-compact" 
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            <span className="progress-stats">{completedSteps} / {totalSteps} {t('journeyHome.stepsCompleted')}</span>
          </div>
        </div>

        {/* Middle: Current Step */}
        {currentStep && (
          <div className="current-step-section">
            <div className="step-badge-container">
              <span className="step-badge">{t('journeyHome.currentStep')}</span>
              <span className="step-number">Step {currentStep.stepIndex + 1}</span>
            </div>
            <h4 className="step-title-compact">{getStepTitle(currentStep)}</h4>
            {nextAction && (
              <div className="next-action-compact">
                <i className="bi bi-arrow-right-circle"></i>
                <span>{t('journeyHome.next')}: {getActionText(nextAction)}</span>
              </div>
            )}
          </div>
        )}

        {/* Right: Actions */}
        <div className="journey-actions-section">
          <button 
            className="journey-btn primary"
            onClick={() => navigate(`/journey/${journey.id}`)}
          >
            <i className="bi bi-play-circle"></i>
            {t('journeyHome.continue')}
          </button>
          {/* <Link to="/journey-list" className="journey-btn secondary">
            <i className="bi bi-list"></i>
            {t('journeyHome.viewAll')}
          </Link> */}
        </div>
      </div>
    </div>
  );
};
